import AreaChart from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/AreaChart";
import { NoEventDataMessage } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/NoEventDataMessage";
import { SectionStateContent } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/SectionStateContent";
import { InfoContent } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/TransportNeed/infoContent";
import { AltTransStretchCoverage } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/types";
import { useMemo } from "react";
import { Circle } from "shared/components/Circle";
import { InfoHeader } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/InfoHeader";
import { Flex, Text, VStack } from "shared/components/Spor";
import RouteSvg from "shared/icons/route.svg?react";
import { useTheme } from "styled-components";
import { useTransportNeedData } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/TransportNeed/useTransportNeedData";
import { Nullish } from "shared/utils/objectUtils";

type TransportNeedProps = {
  selectedStretch: AltTransStretchCoverage | null;
  showAllStretches: boolean;
  incidentId: Nullish<string>;
};

export const TransportNeed = ({
  selectedStretch,
  showAllStretches,
  incidentId,
}: TransportNeedProps) => {
  const theme = useTheme();

  const { maxYValue, aggregatedData, stretchData, status, refetch } =
    useTransportNeedData(incidentId);

  const isLoading = status === "pending";
  const isError = status === "error";
  const isEmpty = !isLoading && !isError && !aggregatedData.length;

  const displayedStretchName = selectedStretch?.stretchName;

  const selectedStretchData = useMemo(() => {
    if (!displayedStretchName) {
      return [];
    }

    const stretchNames = Object.keys(stretchData);

    // 1) Exact match if possible
    const exactMatch = stretchNames.find(
      (stretchName) => stretchName === displayedStretchName,
    );
    if (exactMatch) {
      return stretchData[exactMatch];
    }

    // 2) Fuzzy match on destination part (after "–"), case-insensitive
    const [, titleToRaw = ""] = displayedStretchName.split("–");
    const titleTo = titleToRaw.trim().toLowerCase();

    const fuzzyMatch = stretchNames.find((stretchName) => {
      const [, stretchToRaw = ""] = stretchName.split("–");
      const stretchTo = stretchToRaw.trim().toLowerCase();
      return stretchTo !== "" && stretchTo === titleTo;
    });

    if (fuzzyMatch) {
      return stretchData[fuzzyMatch];
    }

    // 3) Fallback: nothing matched
    return [];
  }, [displayedStretchName, stretchData]);

  return (
    <VStack
      position="relative"
      gap={2}
      backgroundColor={theme.colorBackgroundSecondary}
      padding={2}
      borderRadius="xs"
      height="100%"
      flex="1"
      overflow="hidden"
    >
      <InfoHeader
        title="Maksimalt transportbehov"
        iconColor={theme.colorIconSecondary}
        alignment="right"
        tooltipWidth="360px"
        headerContent={
          <Flex direction="row" alignItems="center">
            {selectedStretch && (
              <>
                <RouteSvg />
                <Text
                  ml={1}
                  mr={2}
                  variant="sm"
                  color={theme.colorTextSecondary}
                >
                  {displayedStretchName}
                </Text>
              </>
            )}
            <Circle size="12px" bg={theme.colorSuccessPulse} />
            <Text mx={1} variant="sm" color={theme.colorTextSecondary}>
              Totalt brudd
            </Text>
          </Flex>
        }
      >
        <InfoContent />
      </InfoHeader>
      <SectionStateContent
        isLoading={isLoading}
        isError={isError}
        isEmpty={isEmpty}
        onRetry={refetch}
        emptyContent={<NoEventDataMessage mt="-2" />}
      >
        <AreaChart
          chosenStretchData={selectedStretchData}
          totalEventData={aggregatedData}
          maxYValue={maxYValue}
          hasResponsiveHeight={showAllStretches}
        />
      </SectionStateContent>
    </VStack>
  );
};
